import React, { FunctionComponent, useState } from 'react';
import { useFormContext, useStepper } from '../../../../hooks';
import { CalculatorBuyerCostsFormFields, CalculatorBuyerCostsResultData } from './types';
import useMediaQuery from '../../../../hooks/use-media';
import { mainMediaMobile } from '../../../../utils';
import { StepperPrevious } from '../../../shared/general';
import { Button } from '@chakra-ui/react';
import { CalculatorBuyerCostsSummary } from './calculator-buyer-costs-summary';
import { FormattedMessage } from 'react-intl';

export const CalculatorBuyerCostsResult: FunctionComponent = () => {
  const { result, updateResult } = useFormContext<
    CalculatorBuyerCostsResultData,
    CalculatorBuyerCostsFormFields
  >();
  const isMobile = useMediaQuery(mainMediaMobile);
  const { steps, currentStep, setCurrentStep, setStepActive } = useStepper();
  const [showCalculation, setShowCalculation] = useState<boolean>(false);

  /*const goToCalculator = () => {
    const queryParams = new URLSearchParams({
      //Klant_Jaarinkomen: form.applicantYearlyIncome,
      //Klant_Leeftijd: form.applicantAge,
      HeeftPartner: form.withPartner ? '1' : '0',
      //Partner_Jaarinkomen: form.partnerYearlyIncome,
      //Partner_Leeftijd: form.partnerAge,
      Student_Debt_Amount: form.housePrice
    });

    window.location.href = `/zelf-berekenen/hoeveel-kan-ik-lenen/calculator/${queryParams}`;
  };*/

  const handleBackClick = () => {
    if (currentStep && currentStep !== 0) {
      const id = currentStep - 1;
      setStepActive(currentStep, true);
      setCurrentStep(id);
      updateResult(null);
    }
  };

  return (
    <div>
      {result && isMobile && currentStep && (
        <div>
          <StepperPrevious
            currentStep={currentStep}
            totalSteps={steps.length}
            title={`Vorige stap`}
            handleBackClick={handleBackClick}
          />
          <div className={`pt-4`}>
            <CalculatorBuyerCostsSummary result={result}></CalculatorBuyerCostsSummary>
          </div>
        </div>
      )}
      <div className={`rounded shadow shadow-shadow-500 bg-white mb-4`}>
        <div className={`p-8 md:flex md:justify-between`}>
          <div>
            <p className={`text-gray-500`}>
              <FormattedMessage id={`result.buyerCosts`} defaultMessage={`Kosten koper`} />
            </p>
            <div className={`text-[40px] tracking-[-1px]`}>
              {result?.buyerCosts}
              <Button
                variant={`link`}
                color={`blue`}
                className={`ml-4`}
                onClick={() => setShowCalculation(!showCalculation)}>
                {!showCalculation && (
                  <FormattedMessage
                    id={`result.calculation.show`}
                    defaultMessage={`Toon berekening`}></FormattedMessage>
                )}
                {showCalculation && (
                  <FormattedMessage
                    id={`result.calculation.hide`}
                    defaultMessage={`Sluit berekening`}></FormattedMessage>
                )}
              </Button>
            </div>

            {showCalculation && (
              <div className="max-w-md rounded shadow p-5 overflow-hidden md:max-w-2xl">
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <FormattedMessage
                        id={`results.calculation.transferTaxExemptionAmount`}
                        defaultMessage={`Overdrachtsbelasting`}
                      />
                    </div>
                    <div className="ml-4">{result?.transferTaxExemptionAmount || '-'}</div>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <FormattedMessage
                        id={`results.calculation.notaryCosts`}
                        defaultMessage={`Notariskosten`}
                      />
                    </div>
                    <div className="ml-4">{result?.notaryCosts || '-'}</div>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <FormattedMessage
                        id={`results.calculation.brokerCommission`}
                        defaultMessage={`Evt. aankoopmakelaarscourtage`}
                      />
                    </div>
                    <div className="ml-4">{result?.brokerCommission || '-'}</div>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <FormattedMessage
                        id={`results.calculation.valuationCosts`}
                        defaultMessage={`Taxatie`}
                      />
                    </div>
                    <div className="ml-4">{result?.valuationCosts || '-'}</div>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <FormattedMessage
                        id={`results.calculation.buildingInspectionCosts`}
                        defaultMessage={`Bouwkundige keuring`}
                      />
                    </div>
                    <div className="ml-4">{result?.buildingInspectionCosts || '-'}</div>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <FormattedMessage
                        id={`results.calculation.nhgCosts`}
                        defaultMessage={`NHG`}
                      />
                    </div>
                    <div className="ml-4">{result?.nhgCosts || '-'}</div>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <FormattedMessage
                        id={`results.calculation.adviceCosts`}
                        defaultMessage={`Advies- & bemiddelingskosten`}
                      />
                    </div>
                    <div className="ml-4">{result?.adviceCosts || '-'}*</div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {result && !isMobile && currentStep && (
            <div>
              <StepperPrevious
                variant={`horizontal`}
                title={`Vorige stap`}
                handleBackClick={handleBackClick}
              />
            </div>
          )}
        </div>
        <hr />
        <div className={`p-8 flex`}>
          <div className={`mr-16`}>
            <p className={`text-gray-500`}>
              <FormattedMessage id="result.houseValue" defaultMessage={`Woningwaarde`} />
            </p>
            <div className={`text-[20px] tracking-[-1px]`}>{result?.housePrice}</div>
          </div>
          <div>
            <p className={`text-gray-500`}>
              <FormattedMessage
                id="result.transferTaxExemptionPercentage"
                defaultMessage={`Overdrachtsbelasting`}
              />
            </p>
            <div className={`text-[20px] tracking-[-1px]`}>
              {result?.transferTaxExemptionPercentage}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className={`my-8`}>
          <FormattedMessage
            id={`result.buyerCostsDisclaimer`}
            defaultMessage={`Deze berekening geeft een indicatie van kosten koper, echter kan dit hoger of lager uitvallen aan de hand van de keuzes die je maakt en de uiteindelijke marktwaarde van de woning. Onze hypotheekadviseurs berekenen de kosten koper zodat je weet waar je aan toe bent.`}
          />
        </div>
        <FormattedMessage
          id={`result.buyerCostsDisclaimerAddOn`}
          defaultMessage={`*De advies- en bemiddelingskosten zijn gebaseerd op een richtprijs voor eerste woning kopen.`}></FormattedMessage>

        {/*<Button onClick={goToCalculator} colorScheme={`orange`}>
          Verder rekenen
        </Button>
        */}
      </div>
    </div>
  );
};
